import {Field} from 'formik';
import _ from 'lodash';
import React, {useEffect, useRef} from 'react';

import {SelectionControl, SelectionControlGroup} from '../SelectionControl';

/**
 * This component is meant to call out one single checkbox:
 *
 * @param {object} param0
 *
 * @param {boolean} [param0.isIndeterminate]
 * Indicates whether the checkbox is in an indeterminate state. This is typically used when the checkbox is part of a
 * group of checkboxes and some are checked while others are not.
 *
 * @param {string} [param0.name]
 * Ensure to include the `name` property when using a Formik-type form control. Without it, the form control may not function properly.
 * Additionally, remember to wrap `<Formik></Formik>` around this and all other form control types to ensure proper functionality.
 * Adding the name to a Formik Input component is essential because Formik uses the name prop to link the value of the  input to the Formik state.
 * Note that the Formik type is the default version injected into the form control you are applying.
 *
 * @param {boolean} [param0.raw]
 * If set to true, a non-Formik version of the form control will be rendered instead.
 *
 * @param {boolean} [param0.disabled]
 * Indicates whether the checkbox is disabled.
 *
 * @param {React.ReactNode} [param0.label]
 * The text to be displayed as the label.
 *
 * @param {any} [param0.value]
 * This represents the initial value of the field.
 *
 * @param {boolean} [param0.checked]
 * Specifies whether the checkbox is checked or not.
 *
 * @param {function} [param0.onChange]
 * Event handler called when the checkbox state changes. It accepts an event object as a parameter, typically used to update the state of the checkbox.
 *
 * @example
 * ```jsx
 *      // == --- How to use this component:  --- ==
 *      <SingleCheckBox label="Is Authentication Required" name="isAuthRequired" />
 * ```
 */
export function SingleCheckBox({raw, id, isIndeterminate, label, value, checked, onChange, name, disabled}) {
    const elementRef = useRef();

    useEffect(() => {
        if (elementRef?.current) {
            // According to the MDN documentation - https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/checkbox#indeterminate_state_checkboxes,
            //  the `indeterminate` property can't be controlled as a prop/HTML attribute, instead it can only be set
            //  via JavaScript. This is why we need to keep a reference to the HTML element and update it manually when
            //  the `isIndeterminate` prop changes.
            elementRef.current.indeterminate = isIndeterminate;
        }
    }, [elementRef, isIndeterminate]);

    if (raw) {
        return (
            <SelectionControl htmlFor={id} label={label} checkbox disabled={disabled}>
                <input
                    ref={elementRef}
                    disabled={disabled}
                    id={id}
                    value={value}
                    type="checkbox"
                    checked={checked}
                    readOnly={_.isNil(onChange)}
                    onChange={onChange}
                />
            </SelectionControl>
        );
    } else {
        return (
            <SelectionControl disabled={disabled} checkbox label={label} htmlFor={'checkbox' + name + value}>
                {name && (
                    <Field
                        innerRef={elementRef}
                        disabled={disabled}
                        name={name}
                        id={'checkbox' + name + value}
                        value={value}
                        type="checkbox"
                    />
                )}
            </SelectionControl>
        );
    }
}

SingleCheckBox.Wrapper = SelectionControlGroup;
